import { useState, useMemo } from "react";
import { 
  Search, 
  FileText, 
  Link as LinkIcon, 
  ExternalLink, 
  Globe, 
  Settings,
  AlertTriangle,
  X
} from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface LinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (link: { text: string; url: string; targetBlank?: boolean; nofollow?: boolean }) => void;
  initialText?: string;
}

export function LinkModal({ isOpen, onClose, onInsert, initialText = "" }: LinkModalProps) {
  const [activeTab, setActiveTab] = useState("artigo");
  const [searchTerm, setSearchTerm] = useState("");
  const [linkText, setLinkText] = useState(initialText);
  const [externalUrl, setExternalUrl] = useState("");
  const [targetBlank, setTargetBlank] = useState(true);
  const [nofollow, setNofollow] = useState(false);

  // Sync linkText with initialText when modal opens or initialText changes
  useMemo(() => {
    if (isOpen) {
      setLinkText(initialText);
    }
  }, [isOpen, initialText]);

  const { data: articles } = useQuery({
    queryKey: ["admin-artigos-links", searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("artigos")
        .select("*, categorias_blog(nome, slug)")
        .order("created_at", { ascending: false });

      if (searchTerm) {
        query = query.or(`titulo.ilike.%${searchTerm}%,palavra_chave.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query.limit(10);
      if (error) throw error;
      return data;
    },
    enabled: isOpen && activeTab === "artigo",
  });

  const sitePages = [
    { label: "Lista de fornecedores", url: "/fornecedores" },
    { label: "Página inicial", url: "/" },
    { label: "Quero anunciar", url: "/quero-anunciar" },
    { label: "Índice de guias", url: "/guias" },
    { label: "Contato", url: "/contato" },
    { label: "Criar conta", url: "/cadastro" },
  ];

  const handleInsert = (url: string) => {
    onInsert({
      text: linkText || "Link",
      url,
      targetBlank: activeTab === "externo" ? targetBlank : false,
      nofollow: activeTab === "externo" ? nofollow : false
    });
    onClose();
    // Reset states
    setExternalUrl("");
  };

  const isCompetitor = (url: string) => {
    const competitors = ["elo7.com.br", "mercadolivre.com.br", "shopee.com.br"];
    return competitors.some(c => url.toLowerCase().includes(c));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Inserir Link</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Texto do link</Label>
            <Input 
              value={linkText} 
              onChange={(e) => setLinkText(e.target.value)} 
              placeholder="Texto que será exibido"
            />
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="artigo">Artigo</TabsTrigger>
              <TabsTrigger value="pagina">Página</TabsTrigger>
              <TabsTrigger value="externo">Externo</TabsTrigger>
            </TabsList>

            <TabsContent value="artigo" className="space-y-4 pt-4">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Buscar artigos..." 
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="max-h-[250px] overflow-y-auto border rounded-md divide-y">
                {articles?.map((art) => (
                  <button
                    key={art.id}
                    onClick={() => handleInsert(`/guias/${art.categorias_blog?.slug || 'geral'}/${art.slug}`)}
                    className="w-full text-left p-3 hover:bg-gray-50 flex flex-col gap-1 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{art.titulo}</span>
                      {art.status === "rascunho" && (
                        <Badge variant="outline" className="text-[10px]">Rascunho</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500">
                      <span>{art.categorias_blog?.nome}</span>
                      <span>•</span>
                      <span>{art.created_at ? format(new Date(art.created_at), "dd MMM, yyyy", { locale: ptBR }) : ''}</span>
                    </div>
                    {art.status === "rascunho" && (
                      <div className="flex items-center gap-1 text-[10px] text-amber-600 font-medium">
                        <AlertTriangle className="w-3 h-3" />
                        Link ficará quebrado até publicar
                      </div>
                    )}
                  </button>
                ))}
                {articles?.length === 0 && (
                  <div className="p-8 text-center text-sm text-gray-500">Nenhum artigo encontrado.</div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="pagina" className="space-y-2 pt-4">
              <div className="grid grid-cols-1 gap-2">
                {sitePages.map((page) => (
                  <Button
                    key={page.url}
                    variant="outline"
                    className="justify-start font-normal"
                    onClick={() => handleInsert(page.url)}
                  >
                    <FileText className="w-4 h-4 mr-2 text-gray-400" />
                    {page.label}
                    <span className="ml-auto text-[10px] text-gray-400">{page.url}</span>
                  </Button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="externo" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>URL Externa</Label>
                <Input 
                  value={externalUrl} 
                  onChange={(e) => setExternalUrl(e.target.value)} 
                  placeholder="https://exemplo.com"
                />
                {isCompetitor(externalUrl) && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 text-amber-700 text-xs rounded-md border border-amber-200">
                    <AlertTriangle className="w-4 h-4" />
                    Aviso: Este link aponta para um concorrente conhecido.
                  </div>
                )}
              </div>
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Abrir em nova aba</Label>
                    <p className="text-[10px] text-gray-500">Adiciona target="_blank"</p>
                  </div>
                  <Switch checked={targetBlank} onCheckedChange={setTargetBlank} />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Marcar como nofollow</Label>
                    <p className="text-[10px] text-gray-500">Indica que você não endossa o link</p>
                  </div>
                  <Switch checked={nofollow} onCheckedChange={setNofollow} />
                </div>
              </div>
              <Button 
                className="w-full bg-primary text-[#0C1A12] font-bold" 
                disabled={!externalUrl.startsWith("http")}
                onClick={() => handleInsert(externalUrl)}
              >
                Inserir Link Externo
              </Button>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}